/**
 * Items v2 (docs/systems/ITEMS_V2.md §5.2): the affix ratings gear adds on top of `deriveStats` (stats.ts; the attribute
 * formulas are untouched). The World's `refreshPlayerStats` adds the older gear stats itself (atk, def, maxHp, crit,
 * speed); this adds the rest that map onto `DerivedStats`. The combat-hook affixes (status chance, guardBreak,
 * qiCostPct …) stay in the equipment sum for their own systems (R2).
 */
import {
  equipmentStats,
  ITEM_STAT_KEYS,
  itemOf,
  type EquipmentStats,
  type EquipSlot,
  type ItemRoll,
} from './data/items';
import type { DerivedStats } from './stats';

/** The derived-stat ratings gear adds to directly. */
const ADDS = [
  'magic',
  'hit',
  'magicHit',
  'magicCrit',
  'magicDef',
  'dodge',
  'toughness',
  'maxQi',
  'maxStamina',
  'qiRegen',
  'hpRegen',
] as const satisfies readonly (keyof DerivedStats & keyof EquipmentStats)[];

/**
 * The last gear sum each player entity was refreshed with (systems/gearCombat.ts reads it at the hit hooks). Keyed per
 * entity, so a World with many players (docs/ONLINE.md §2) keeps each one's gear.
 */
const GEAR = new WeakMap<object, EquipmentStats>();
export const gearFor = (owner: object): EquipmentStats | undefined => GEAR.get(owner);

/**
 * Adds the gear's affix ratings to derived stats, in place. Gear with none of them leaves `d` unchanged. With an
 * `owner` (the player entity), remembers the sum for `gearFor`.
 */
export function addGearStats(d: DerivedStats, gear: EquipmentStats, owner?: object): DerivedStats {
  if (owner) GEAR.set(owner, gear);
  for (const k of ADDS) {
    const v = gear[k];
    if (v) d[k] = Math.round((d[k] + v) * 1000) / 1000;
  }
  return d;
}

/** One line of a gear comparison: a stat (or an art / school bonus) and how much it changes. */
export interface GearDelta {
  /** An `ItemStats` key, or `art:<id>` / `school:<id>`. */
  readonly key: string;
  readonly d: number;
}

/**
 * What wearing `id` instead of what is in its slot changes in the equipped sum (docs/systems/ITEMS_V2.md §5.5 compare): the
 * shared `equipmentStats` on a cloned equipment map, so the client does no stat math. Empty when nothing changes or
 * the item is unknown.
 */
export function compareGear(
  equipment: Partial<Record<EquipSlot, string>>,
  rolls: Readonly<Record<string, ItemRoll>>,
  id: string,
): GearDelta[] {
  const it = itemOf(rolls, id);
  if (!it) return [];
  const now = equipmentStats(equipment, rolls);
  const next = equipmentStats({ ...equipment, [it.slot]: id }, rolls);
  const out: GearDelta[] = [];
  const r = (x: number) => Math.round(x * 1e6) / 1e6;
  for (const k of ITEM_STAT_KEYS) {
    const d = r(next[k] - now[k]);
    if (d) out.push({ key: k, d });
  }
  for (const [kind, a, b] of [
    ['art', now.arts, next.arts],
    ['school', now.schools, next.schools],
  ] as const)
    for (const k of new Set([...Object.keys(a), ...Object.keys(b)])) {
      const d = r((b[k] ?? 0) - (a[k] ?? 0));
      if (d) out.push({ key: `${kind}:${k}`, d });
    }
  return out;
}
